import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps, User } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/MembersPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member extends User {
  role: string;
}

const MembersPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [users, setUsers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Members", "/members");
      hasTrackedRef.current = true;
    }
  }, []);

  // Fetch users whenever selectedOrgID or token changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/organizations/${selectedOrgID}/users`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch users.");
          return;
        }

        const data: Member[] = await response.json();
        setUsers(data);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (selectedOrgID) {
      fetchUsers();
    }
  }, [selectedOrgID, token]);

  const handleDeleteClick = (user: Member) => {
    setMemberToDelete(user);
  };

  const handleRemoveUser = async (userID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/organizations/${selectedOrgID}/users/${userID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to remove user.");
        return;
      }

      setUsers(users.filter((u) => u.user_id !== userID));
      setMemberToDelete(null);
    } catch (err) {
      console.error("Error removing user:", err);
    }
  };

  const handleUpdateRole = async (userID: number, newRole: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/organizations/${selectedOrgID}/users/${userID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(errorData.error || "Failed to update role.");
        return;
      }

      setUsers(users.map((u) => (u.user_id === userID ? { ...u, role: newRole } : u)));
    } catch (err) {
      console.error("Error updating role:", err);
    }
  };

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.display_name && user.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
  ).sort((a, b) => {
    const aName = a.display_name || a.username;
    const bName = b.display_name || b.username;
    return aName.toLowerCase().localeCompare(bName.toLowerCase());
  });

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Members</h1>
      
      <div className={styles.searchContainer}>
        <input
          type="text"
          id="search"
          className={styles.searchInput}
          placeholder="Search by username or display name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className={styles.relatedSection}>
        <h4 className={styles.relatedTitle}>Related</h4>
        <ul className={styles.relatedList}>
          <li className={styles.relatedItem}>
            <a href="/groups" className={styles.relatedLink}>Groups</a>
          </li>
          <li className={styles.relatedItem}>
            <a href="/invites" className={styles.relatedLink}>Manage Invites</a>
          </li>
        </ul>
      </div>

      {memberToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setMemberToDelete(null)} />
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              Are you sure you want to remove <em>{memberToDelete.display_name || memberToDelete.username}</em> from this organization?
              <br /><br />
              This action CANNOT BE UNDONE.
            </div>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleRemoveUser(memberToDelete.user_id)} className={styles.dialogButton}>
                Yes, Remove
              </button>
              <button onClick={() => setMemberToDelete(null)} className={styles.dialogCancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Name</th>
            <th className={styles.tableHeader}>Role</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((u) => (
              <tr key={u.user_id} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  <a
                    href="#"
                    className={styles.tableLink}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      navigate(`/members/${u.user_id}`);
                    }}
                  >
                    {u.display_name ? `${u.display_name} (${u.username})` : u.username}
                  </a>
                </td>
                <td className={styles.tableCell}>
                  <select
                    value={u.role}
                    className={styles.select}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdateRole(u.user_id, e.target.value)}
                  >
                    <option value="member">Member</option>
                    <option value="owner">Owner</option>
                  </select>
                </td>
                <td className={styles.tableCell}>
                  <button 
                    onClick={() => handleDeleteClick(u)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className={styles.tableCell}>No users found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MembersPage;
